import { useCallback, useEffect, useMemo, useRef, useState, MutableRefObject } from 'react';
import { conversationSummaryOf } from '@zunelo/shared';
import type {
  AttentionItem,
  Conversation,
  ConversationAnchor,
  ConversationSummary,
} from '../../domain/types';
import { pruneChatHistory } from '../../domain/chatHistory';
import {
  getAllThreadChatHistories,
  getThreadChatHistory,
  isChatMigrationDone,
  markChatMigrationDone,
  removeThreadChatHistory,
} from '../../services/chatHistoryStore';
import { conversationsClient, slackClient, workspaceClient } from '../../services/clients';
import { toErrorMessage } from '../../services/errors';

export interface ConversationsSliceOptions {
  itemsRef: MutableRefObject<AttentionItem[]>;
  selectItem: (id: string | null) => void;
}

const MIGRATION_SEED_LIMIT = 50;
const ASK_CONTEXT_MESSAGE_LIMIT = 10;
const ASK_CONTEXT_SUMMARY_CHARS = 800;

function anchorToConversationAnchor(anchor: {
  itemId: string;
  channelName: string;
  title: string;
  summary?: string;
}): ConversationAnchor {
  return {
    itemId: anchor.itemId,
    source: 'slack',
    channelName: anchor.channelName,
    title: anchor.title,
    ...(anchor.summary ? { summary: anchor.summary } : {}),
  };
}

function resolveSlackThreadKey(
  itemsRef: MutableRefObject<AttentionItem[]>,
  itemId: string,
): { channelName: string; threadKey: string } {
  const item = itemsRef.current.find((entry) => entry.id === itemId);
  const thread = item?.rawSlackThread;
  return {
    channelName: item?.primary || thread?.channel.name || itemId,
    threadKey: thread?.attentionId || thread?.threadTs || itemId,
  };
}

function conversationContextSummary(conversation: Conversation | null): string {
  if (!conversation) return '';
  return conversation.messages
    .slice(-ASK_CONTEXT_MESSAGE_LIMIT)
    .map((m) => `${m.role === 'user' ? 'Usuario' : 'Asistente'}: ${m.text.slice(0, ASK_CONTEXT_SUMMARY_CHARS)}`)
    .join('\n');
}

function openChatErrorMessage(err: unknown): string {
  return toErrorMessage(err, 'No se pudo abrir la conversación.');
}

export function useConversationsSlice({ itemsRef, selectItem }: ConversationsSliceOptions) {
  const [conversations, setConversations] = useState<ConversationSummary[]>([]);
  const [conversationsLoading, setConversationsLoading] = useState(false);
  const [activeConversationId, setActiveConversationId] = useState<string | null>(null);
  const [activeConversation, setActiveConversation] = useState<Conversation | null>(null);
  const [isConversationLoading, setIsConversationLoading] = useState(false);
  const initialChatParams = typeof window === 'undefined' ? null : new URLSearchParams(window.location.search);
  const [isChatOpen, setIsChatOpenState] = useState(initialChatParams?.get('chat') === 'open');
  const [isChatSending, setIsChatSending] = useState(false);
  const [chatError, setChatError] = useState<string | null>(null);
  const chatRequestIdRef = useRef(0);
  const migrationInFlightRef = useRef(false);
  const mountedRef = useRef(true);

  const setIsChatOpen = useCallback((open: boolean, conversationId?: string | null) => {
    setIsChatOpenState(open);
    if (typeof window === 'undefined') return;
    const url = new URL(window.location.href);
    if (open) url.searchParams.set('chat', 'open'); else url.searchParams.delete('chat');
    if (open && conversationId) url.searchParams.set('conversation', conversationId); else url.searchParams.delete('conversation');
    window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
  }, []);

  const refreshConversations = useCallback(async (): Promise<ConversationSummary[] | null> => {
    try {
      const res = await conversationsClient.getConversations();
      if (!mountedRef.current) return null;
      setConversations(res.data);
      return res.data;
    } catch {
      if (mountedRef.current) setConversations([]);
      return null;
    }
  }, []);

  const migrateLocalThreadHistories = useCallback(async (): Promise<void> => {
    if (migrationInFlightRef.current) return;
    migrationInFlightRef.current = true;
    try {
      const transcripts = getAllThreadChatHistories();
      if (transcripts.length === 0) {
        markChatMigrationDone();
        return;
      }
      const seededByAnchor = new Set<string>();
      const seedList = await refreshConversations();
      for (const entry of seedList ?? []) {
        if (entry.anchor?.itemId) seededByAnchor.add(entry.anchor.itemId);
      }
      const handled: string[] = [];
      for (const entry of transcripts) {
        if (seededByAnchor.has(entry.itemId)) {
          handled.push(entry.itemId);
          continue;
        }
        const item = itemsRef.current.find((candidate) => candidate.id === entry.itemId);
        const anchor: ConversationAnchor = {
          itemId: entry.itemId,
          source: 'slack',
          channelName: item?.primary || entry.itemId,
          title: item?.title || entry.itemId,
          ...(item?.ai ? { summary: item.ai } : {}),
        };
        await conversationsClient.createConversation({
          anchor,
          seedMessages: pruneChatHistory(entry.messages, MIGRATION_SEED_LIMIT),
        });
        handled.push(entry.itemId);
        seededByAnchor.add(entry.itemId);
        // Re-sync the anchor index after each create so a concurrent tab's
        // seeds are never duplicated.
        const updatedList = await refreshConversations();
        for (const entry2 of updatedList ?? []) {
          if (entry2.anchor?.itemId) seededByAnchor.add(entry2.anchor.itemId);
        }
      }
      // Only remove transcripts that were successfully migrated; a mid-loop
      // failure keeps the remainder local for the next boot.
      for (const itemId of handled) removeThreadChatHistory(itemId);
      if (handled.length === transcripts.length) markChatMigrationDone();
      await refreshConversations();
    } catch {
      // Migration is best-effort; remaining transcripts stay local for retry.
    } finally {
      migrationInFlightRef.current = false;
    }
  }, [refreshConversations, itemsRef]);

  const loadConversation = useCallback(async (id: string) => {
    setIsConversationLoading(true);
    try {
      const res = await conversationsClient.getConversation(id);
      if (!mountedRef.current) return;
      setActiveConversation(res.data);
      setActiveConversationId(id);
      setChatError(null);
    } catch (err) {
      if (!mountedRef.current) return;
      setActiveConversation(null);
      setActiveConversationId(null);
      setChatError(openChatErrorMessage(err));
    } finally {
      if (mountedRef.current) setIsConversationLoading(false);
    }
  }, []);

  const answerWithThreadContext = useCallback(async (
    anchor: ConversationAnchor,
    question: string,
  ): Promise<string> => {
    const { channelName, threadKey } = resolveSlackThreadKey(itemsRef, anchor.itemId);
    const res = await slackClient.askAboutThread(channelName, threadKey, question);
    return res.data.answer?.trim() || 'No hay respuesta disponible.';
  }, [itemsRef]);

  const answerWithConversationContext = useCallback(async (
    conversation: Conversation | null,
    question: string,
  ): Promise<string> => {
    const summary = conversationContextSummary(conversation);
    const res = await workspaceClient.askRadarAssistant({
      question,
      contexts: [
        {
          key: conversation ? `conv-${conversation.id}` : 'conversacion',
          title: conversation?.title || 'Conversación Zunelo',
          summary,
          nextStep: '',
          sources: ['conversación Zunelo'],
        },
      ],
    });
    return res.data.answer?.trim() || 'No hay respuesta disponible.';
  }, []);

  const saveUserMessage = useCallback(async (
    requestId: number,
    conversation: Conversation,
    text: string,
  ): Promise<Conversation | null> => {
    if (requestId !== chatRequestIdRef.current) return null;
    try {
      const userRes = await conversationsClient.addMessage(conversation.id, { role: 'user', text });
      if (requestId !== chatRequestIdRef.current) return null;
      if (mountedRef.current) {
        setActiveConversation(userRes.data);
        setActiveConversationId(userRes.data.id);
      }
      await refreshConversations();
      return userRes.data;
    } catch (err) {
      if (requestId !== chatRequestIdRef.current) return null;
      setChatError(toErrorMessage(err, 'No se pudo guardar tu pregunta.'));
      throw err;
    }
  }, [refreshConversations]);

  const saveAssistantReply = useCallback(async (
    requestId: number,
    conversation: Conversation,
    conversationAtSend: Conversation,
    question: string,
  ): Promise<void> => {
    try {
      const answer = await (conversation.anchor
        ? answerWithThreadContext(conversation.anchor, question)
        : answerWithConversationContext(conversationAtSend, question));
      if (requestId !== chatRequestIdRef.current) return;
      const assistantRes = await conversationsClient.addMessage(conversation.id, {
        role: 'assistant',
        text: answer,
      });
      if (requestId !== chatRequestIdRef.current) return;
      if (mountedRef.current) setActiveConversation(assistantRes.data);
      await refreshConversations();
    } catch (err) {
      if (requestId !== chatRequestIdRef.current) return;
      if (mountedRef.current) setChatError(toErrorMessage(err, 'No se pudo responder con la IA.'));
      try {
        const assistantRes = await conversationsClient.addMessage(conversation.id, {
          role: 'assistant',
          text: toErrorMessage(err, 'No se pudo responder con la IA.'),
        });
        if (mountedRef.current) setActiveConversation(assistantRes.data);
      } catch {
        // The transcript stays clean; the error is already surfaced above.
      }
    }
  }, [answerWithThreadContext, answerWithConversationContext, refreshConversations]);

  const openThreadChat = useCallback(async (anchor: {
    itemId: string;
    channelName: string;
    title: string;
    summary?: string;
  }) => {
    chatRequestIdRef.current += 1;
    const requestId = chatRequestIdRef.current;
    setIsChatSending(false);
    setChatError(null);
    selectItem(anchor.itemId);
    const existing = conversations.find((c) => c.anchor?.itemId === anchor.itemId);
    let targetId = existing?.id;
    if (!targetId) {
      try {
        const saved = getThreadChatHistory(anchor.itemId);
        const res = await conversationsClient.createConversation({
          anchor: anchorToConversationAnchor(anchor),
          ...(saved.length > 0 ? { seedMessages: pruneChatHistory(saved, MIGRATION_SEED_LIMIT) } : {}),
        });
        targetId = res.data.id;
        if (mountedRef.current) {
          setConversations((prev) => [conversationSummaryOf(res.data), ...prev]);
        }
      } catch (err) {
        if (mountedRef.current) setChatError(openChatErrorMessage(err));
        return;
      }
    }
    // A close/switch during the async create must not reopen the chat.
    if (!mountedRef.current || requestId !== chatRequestIdRef.current) return;
    setActiveConversationId(targetId ?? null);
    setIsChatOpen(true, targetId);
    if (targetId) await loadConversation(targetId);
  }, [selectItem, conversations, setIsChatOpen, loadConversation]);

  const selectConversation = useCallback(async (id: string) => {
    chatRequestIdRef.current += 1;
    setIsChatSending(false);
    setIsChatOpen(true, id);
    setChatError(null);
    await loadConversation(id);
  }, [setIsChatOpen, loadConversation]);

  const startNewChat = useCallback(() => {
    chatRequestIdRef.current += 1;
    setIsChatSending(false);
    setActiveConversationId(null);
    setActiveConversation(null);
    setChatError(null);
    setIsChatOpen(true);
  }, [setIsChatOpen]);

  const closeChat = useCallback(() => {
    chatRequestIdRef.current += 1;
    setIsChatSending(false);
    setIsChatOpen(false);
  }, [setIsChatOpen]);

  const ensureActiveConversation = useRef<() => Promise<Conversation | null>>(async () => null);
  ensureActiveConversation.current = async (): Promise<Conversation | null> => {
    if (activeConversation) return activeConversation;
    const res = await conversationsClient.createConversation({});
    if (!mountedRef.current) return null;
    return res.data;
  };

  const sendChatQuestion = useCallback(async (question: string): Promise<void> => {
    const trimmed = question.trim();
    if (!trimmed || isChatSending || isConversationLoading) return;
    chatRequestIdRef.current += 1;
    const requestId = chatRequestIdRef.current;
    setIsChatSending(true);
    setChatError(null);

    let conversation: Conversation | null;
    try {
      conversation = await ensureActiveConversation.current();
    } catch (err) {
      if (requestId === chatRequestIdRef.current) {
        setChatError(openChatErrorMessage(err));
        setIsChatSending(false);
      }
      throw err;
    }
    if (!conversation || requestId !== chatRequestIdRef.current) {
      setIsChatSending(false);
      return;
    }
    if (mountedRef.current) {
      setActiveConversationId(conversation.id);
      setActiveConversation(conversation);
      setIsChatOpen(true, conversation.id);
    }
    await refreshConversations();

    const conversationAtSend = conversation;
    try {
      conversation = await saveUserMessage(requestId, conversation, trimmed);
      if (!conversation) return;
      await saveAssistantReply(requestId, conversation, conversationAtSend, trimmed);
    } finally {
      if (requestId === chatRequestIdRef.current) {
        setIsChatSending(false);
      }
    }
  }, [isChatSending, isConversationLoading, setIsChatOpen, refreshConversations, saveUserMessage, saveAssistantReply]);

  const deleteConversation = useCallback(async (id: string) => {
    try {
      await conversationsClient.deleteConversation(id);
    } catch {
      // Deletion is best-effort; the list refresh converges with the server.
    }
    if (mountedRef.current) {
      setConversations((prev) => prev.filter((c) => c.id !== id));
      if (activeConversationId === id) {
        chatRequestIdRef.current += 1;
        setIsChatSending(false);
        setChatError(null);
        setActiveConversationId(null);
        setActiveConversation(null);
        setIsChatOpen(false);
      }
    }
  }, [activeConversationId, setIsChatOpen]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    const onPopState = () => {
      const params = new URLSearchParams(window.location.search);
      setIsChatOpenState(params.get('chat') === 'open');
      const conversationId = params.get('conversation');
      if (params.get('chat') === 'open' && conversationId) void loadConversation(conversationId);
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, [loadConversation]);

  useEffect(() => {
    let cancelled = false;
    const boot = async () => {
      setConversationsLoading(true);
      const list = await refreshConversations();
      if (cancelled) return;
      // Migrate only when the cloud list actually loaded (a fetch failure
      // returns null and must not trigger a destructive migration) and the
      // local marker is absent. Anchor dedup makes retries and cross-device
      // boots idempotent.
      if (list !== null && !isChatMigrationDone() && mountedRef.current) {
        await migrateLocalThreadHistories();
      }
      setConversationsLoading(false);
    };
    void boot();
    return () => {
      cancelled = true;
    };
  }, [refreshConversations, migrateLocalThreadHistories]);

  useEffect(() => {
    const conversationId = initialChatParams?.get('conversation');
    if (initialChatParams?.get('chat') === 'open' && conversationId) {
      void loadConversation(conversationId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const chatMessages = activeConversation?.messages ?? [];

  return useMemo(
    () => ({
      conversations,
      conversationsLoading,
      activeConversation,
      activeConversationId,
      isConversationLoading,
      selectConversation,
      startNewChat,
      openThreadChat,
      closeChat,
      deleteConversation,
      isChatOpen,
      chatMessages,
      chatAnchor: activeConversation?.anchor ?? null,
      isChatSending,
      chatError,
      sendChatQuestion,
    }),
    [
      conversations,
      conversationsLoading,
      activeConversation,
      activeConversationId,
      isConversationLoading,
      selectConversation,
      startNewChat,
      openThreadChat,
      closeChat,
      deleteConversation,
      isChatOpen,
      chatMessages,
      isChatSending,
      chatError,
      sendChatQuestion,
    ],
  );
}
